const state = {
  leftPanelCollapsed: false,
  rightPanelCollapsed: false,
  showChangePasswordModal: false,
  showLicenseModal: false,
  showCreateProjectModal: false,
  selectedImage: null,
  selectedImageType: null, // 'prospective' or 'retrospective'
  imageIndex: null, // Current index in filtered images
  totalImages: 0, // Total number of filtered images
  // Labeler state
  labelerMode: false, // Whether labeler is active in CentralContent
  labelerProjectId: null, // Project ID for labeling
  labelerImageId: null, // Specific image ID (null = next unlabeled)
  labelerPreviousView: null, // Previous view to restore when exiting labeler
  updatedImage: {
    id: null,
    value: null,
    action: null
  },
  evaluationFilterChoice: 'All',
  evaluationWithModeration: false
}

const getters = {
  leftPanelWidth: (state) => {
    const navWidth = state.rightPanelCollapsed ? '70px' : '250px'
    const panelWidth = state.leftPanelCollapsed ? '70px' : '33.333%'
    return `calc(${panelWidth} + ${navWidth})`
  },
  rightPanelWidth: () => {
    return '0px'
  }
}

const actions = {
  toggleLeftPanel ({ commit }) {
    commit('toggleLeftPanel')
  },
  toggleRightPanel ({ commit }) {
    commit('toggleRightPanel')
  },
  setLeftPanelCollapsed ({ commit }, collapsed) {
    commit('setLeftPanelCollapsed', collapsed)
  },
  setRightPanelCollapsed ({ commit }, collapsed) {
    commit('setRightPanelCollapsed', collapsed)
  },
  setShowChangePasswordModal ({ commit }, show) {
    commit('setShowChangePasswordModal', show)
  },
  setShowLicenseModal ({ commit }, show) {
    commit('setShowLicenseModal', show)
  },
  setShowCreateProjectModal ({ commit }, show) {
    commit('setShowCreateProjectModal', show)
  },
  setSelectedImage ({ commit }, payload) {
    commit('setSelectedImage', payload)
  },
  clearSelectedImage ({ commit }) {
    commit('clearSelectedImage')
  },
  enterLabelerMode ({ commit, state }, { projectId, imageId = null }) {
    // Save current view to restore later
    const previousView = {
      selectedImage: state.selectedImage,
      selectedImageType: state.selectedImageType,
      imageIndex: state.imageIndex,
      totalImages: state.totalImages
    }

    commit('setLabelerMode', {
      active: true,
      projectId,
      imageId,
      previousView
    })
  },
  exitLabelerMode ({ commit, state, dispatch }) {
    // Clear labeler state
    commit('setLabelerMode', {
      active: false,
      projectId: null,
      imageId: null,
      previousView: null
    })

    // Restore previous view if it existed
    if (state.labelerPreviousView?.selectedImage) {
      dispatch('setSelectedImage', {
        image: state.labelerPreviousView.selectedImage,
        imageType: state.labelerPreviousView.selectedImageType,
        imageIndex: state.labelerPreviousView.imageIndex,
        totalImages: state.labelerPreviousView.totalImages
      })
    }
  },
  setUpdatedImage ({ commit }, payload) {
    commit('setUpdatedImage', payload)
  },
  clearUpdatedImage ({ commit }) {
    commit('clearUpdatedImage')
  },
  setEvaluationFilterChoice ({ commit }, choice) {
    commit('setEvaluationFilterChoice', choice)
  },
  setEvaluationWithModeration ({ commit }, val) {
    commit('setEvaluationWithModeration', val)
  }
}

// Helper to trigger chart resize after panel transitions
const triggerChartsResize = () => {
  // Delay to allow CSS transition to complete (300ms typical transition + buffer)
  setTimeout(() => {
    window.dispatchEvent(new Event('resize'))
  }, 350)
}

const mutations = {
  toggleLeftPanel (state) {
    state.leftPanelCollapsed = !state.leftPanelCollapsed
    triggerChartsResize()
  },
  toggleRightPanel (state) {
    state.rightPanelCollapsed = !state.rightPanelCollapsed
    triggerChartsResize()
  },
  setLeftPanelCollapsed (state, collapsed) {
    state.leftPanelCollapsed = collapsed
    triggerChartsResize()
  },
  setRightPanelCollapsed (state, collapsed) {
    state.rightPanelCollapsed = collapsed
    triggerChartsResize()
  },
  setShowChangePasswordModal (state, show) {
    state.showChangePasswordModal = show
  },
  setShowLicenseModal (state, show) {
    state.showLicenseModal = show
  },
  setShowCreateProjectModal (state, show) {
    state.showCreateProjectModal = show
  },
  setSelectedImage (state, payload) {
    state.selectedImage = payload.image
    state.selectedImageType = payload.imageType
    state.imageIndex = payload.imageIndex || null
    state.totalImages = payload.totalImages || 0
  },
  clearSelectedImage (state) {
    state.selectedImage = null
    state.selectedImageType = null
    state.imageIndex = null
    state.totalImages = 0
  },
  setLabelerMode (state, { active, projectId, imageId, previousView }) {
    state.labelerMode = active
    state.labelerProjectId = projectId
    state.labelerImageId = imageId
    state.labelerPreviousView = previousView
  },
  setUpdatedImage (state, payload) {
    state.updatedImage = payload
  },
  clearUpdatedImage (state) {
    state.updatedImage = {
      id: null,
      value: null,
      action: null
    }
  },
  setEvaluationFilterChoice (state, choice) {
    state.evaluationFilterChoice = choice
  },
  setEvaluationWithModeration (state, val) {
    state.evaluationWithModeration = val
  }
}

export const workspacePanel = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
